import axios from 'axios';

const env = import.meta.env;

// REACT_APP_API_URL is the full base, including the API version (e.g.
// https://dev-api.perkvora.com/api/v1). Keeping the version in env makes
// it easy to point a build at /api/v2 later without touching code.
const api = axios.create({
  baseURL: env.REACT_APP_API_URL,
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
});

api.interceptors.request.use((cfg) => {
  // Tenant resolution happens server-side via the request's Origin header,
  // matched against app_clients.allowed_origins. No client-side API key is
  // needed — keeps secrets out of the bundle and lets keys rotate without
  // a frontend redeploy. The Authorization bearer carries the per-user
  // sanctum token after login (independent from tenant resolution).
  const token = localStorage.getItem('pv_token');
  if (token) cfg.headers['Authorization'] = `Bearer ${token}`;
  else delete cfg.headers['Authorization'];
  return cfg;
});

// Paths where the user is explicitly not logged in (or trying to recover),
// so a background 401 must NOT yank them back to /login. Without this, a
// stale-token user lands on /login, clicks "Forgot?" → Navbar's unread-count
// poll fires → 401 → this interceptor hard-redirects them back to /login,
// making the forgot-password page unreachable at the exact moment they need it.
const PUBLIC_AUTH_PATHS = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-otp',
  '/newsletter/unsubscribe',
];

function onPublicAuthPath() {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return PUBLIC_AUTH_PATHS.some(prefix => p === prefix || p.startsWith(prefix + '/'));
}

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      const hadToken = !!localStorage.getItem('pv_token');
      localStorage.removeItem('pv_token');
      localStorage.removeItem('pv_user');
      // Force a hard reload to /login so AuthContext drops its in-memory user
      // and protected routes don't keep rendering against a dead session.
      // Skip on public auth pages — see PUBLIC_AUTH_PATHS above.
      if (hadToken && !onPublicAuthPath()) {
        window.location.assign('/login');
      }
    }
    return Promise.reject(err);
  }
);

export default api;
